import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useMounted } from '../../../hooks/useMounted';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { setRef } from '../../../util/ref-util';
import { Input } from '../../Input';
import { jsx as _jsx } from "react/jsx-runtime";
export const IntegerInput = /*#__PURE__*/forwardRef(function IntegerInput(props, ref) {
  const [value, setValue] = useState(props.isDuration ? props.value.toLocaleString().replace(/[,.]/g, '') : String(props.value));
  const [editValue, setEditValue] = useState(props.isDuration ? props.value.toLocaleString().replace(/[,.]/g, '') : String(props.value));
  /**
   * Possible values:
   * - **unfocused**: the user isn't editing the value
   * - **editing**: the user is editing the value
   * - **committed**: the user just committed a value but is still editing. This state should have stricter
   *   validation than regular editing.
   */

  const [editState, setEditState] = useState('unfocused');
  const isValid = isValidInteger(editValue, {
    minValue: props.minValue,
    maxValue: props.maxValue,
    isEditing: editState === 'editing',
    isDuration: props.isDuration ?? false
  });
  const refInput = useRef(null);
  useImperativeHandle(ref, () => ({
    isValid: () => isValid,
    focus: () => refInput.current?.focus()
  }));
  const localize = useNCTextLocalizer('DatePicker');
  const mountedRef = useMounted();
  useEffect(() => {
    function handleInputKeyDown() {
      setEditState(prevState => prevState !== 'committed' ? 'editing' : prevState);
    }
    const input = refInput.current;
    input?.addEventListener('keydown', handleInputKeyDown);
    return function () {
      input?.removeEventListener('keydown', handleInputKeyDown);
    };
  });
  return /*#__PURE__*/_jsx(Input, {
    ref: element => {
      refInput.current = element;
      setRef(props.inputRef, element);
    },
    id: props.id,
    testId: props.testId,
    className: props.className,
    style: props.style,
    ariaLabel: props.ariaLabel,
    maxLength: Math.max(String(props.minValue).length, String(props.maxValue).length),
    value: editState !== 'unfocused' || !isValid ? editValue : value,
    status: isValid ? undefined : 'alert',
    message: !isValid ? localize(props.invalidMessageKey ?? 'invalidInteger.msg', {
      min: String(props.minValue),
      max: String(props.maxValue)
    }) : undefined,
    onFocus: () => {
      if (isValid) {
        setEditValue(value);
      }
    },
    onBlur: () => {
      // NOTE: input uses `useOnFocusOut` when it probably doesn't need to.
      //     | the issue with using that hook is that it causes onCommit to be fired after onBlur.
      // NOTE: it probably doesn't need to use that hook because it's popover probably shouldn't receive focus,
      //     | it also doesn't receive focus in this case because there are no suggestions for this input.
      setTimeout(() => setEditState('unfocused'), 0);
    },
    onChange: event => {
      setValue(event.value);
      setEditValue(event.value);
      setEditState(prevState => prevState !== 'unfocused' ? 'editing' : prevState);
      if (event.value.length > 0 && (!props.isDuration || isValid)) {
        props.onChange(parseInteger(event.value, props.isDuration ?? false), 'onChange');
      }
    },
    onCommit: event => {
      if (!mountedRef.current) return;
      setEditState(prevState => prevState === 'editing' ? 'committed' : prevState);
      const value = parseInteger(event.value, props.isDuration ?? false);
      if (!isNaN(value) && (!props.isDuration || isValid)) {
        props.onChange(value, 'onCommit');
      }
    }
  });
});
const validIntegerString = /^\s*[0-9]+\s*$/u;
const validDurationIntegerString = /^-?\s*[0-9]+\s*$/u;
function parseInteger(value, isDuration) {
  if (isDuration) return validDurationIntegerString.test(value) ? Number.parseInt(value, 10) : NaN;
  return validIntegerString.test(value) ? Number.parseInt(value, 10) : NaN;
}
function isValidInteger(value, options) {
  const parsedValue = parseInteger(value, options.isDuration);
  if (options.isEditing) {
    if (options.isDuration && value.lastIndexOf('-') === 0) return true;
    if (value.includes(' ')) return false;
    const len = value.trim().length;
    const maxLength = Math.max(String(options.minValue).length, String(options.maxValue).length);
    return (!isNaN(parsedValue) || len === 0) && (len < maxLength || inBounds(parsedValue, options));
  } else {
    return !isNaN(parsedValue) && inBounds(parsedValue, options);
  }
}
function inBounds(value, options) {
  return options.minValue <= value && value <= options.maxValue;
}
//# sourceMappingURL=IntegerInput.js.map